import { Button, Slider } from "@heroui/react";
import { IconCameraRotate, IconFlipVertical } from "@tabler/icons-react";
import { type FC, useState } from "react";
import type { Area, Point } from "react-easy-crop";
import ReactCrop from "react-easy-crop";

interface PhotoCropperProps {
    photo: string;
    onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void;
    onClose: () => void;
    onSave: () => void;
    onRetake: () => void;
    isCameraPicture?: boolean;
    onMirror: () => void;
}

export const PhotoCropper: FC<PhotoCropperProps> = ({
    photo,
    onCropComplete,
    onClose,
    onSave,
    onRetake,
    onMirror,
    isCameraPicture = false,
}) => {
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const handleReactCropChange = (newCrop: Point) => {
        setCrop(newCrop);
    };

    const handleZoomChange = (value: number | number[]) => {
        // Assuming value can be an array or number; adapt based on actual library behavior
        const newZoom = Array.isArray(value) ? value[0] : value;
        setZoom(newZoom);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="relative w-[500px] rounded-lg bg-white p-4">
                <div className="relative h-[500px]">
                    <ReactCrop
                        image={photo}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={handleReactCropChange}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        cropShape="rect"
                    />
                </div>
                <div className="mt-4 flex flex-col gap-4 bg-white">
                    <div className="hidden items-center gap-2 md:flex">
                        <span className="text-gray-600 text-sm">Zoom:</span>
                        <Slider
                            minValue={1}
                            maxValue={3}
                            step={0.01}
                            value={zoom}
                            onChange={handleZoomChange}
                            className="w-full"
                            size="lg"
                        />
                        {isCameraPicture && (
                            <>
                                <Button
                                    variant="flat"
                                    onPress={onMirror}
                                    className="ml-2"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconFlipVertical stroke={1.5} size={20} />
                                </Button>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        {isCameraPicture && (
                            <>
                                <Button
                                    variant="flat"
                                    onPress={onMirror}
                                    className="md:hidden"
                                    isIconOnly={true}
                                >
                                    <IconFlipVertical stroke={1.5} size={20} />
                                </Button>
                                <Button
                                    variant="flat"
                                    onPress={onRetake}
                                    className="mr-auto"
                                    isIconOnly={true}
                                >
                                    <IconCameraRotate stroke={1.5} size={20} />
                                </Button>
                            </>
                        )}
                        <Button
                            color="danger"
                            variant="light"
                            onPress={onClose}
                        >
                            Abbrechen
                        </Button>
                        <Button color="primary" onPress={onSave}>
                            Speichern
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
